import { createContext, useContext, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
    GET_PROJECT_SYSTEMS_MENU,
    GET_PROJECT_CRA_COUNT,
} from '@api/queries/projects';
import { useQueryConnection } from '@models/queries/useQueryConnection';
import { type QueryConnection } from '@models/queries/models/QueryConnection';

type AnyQueryConnection = QueryConnection<any, any>;

interface GlobalDataContextValue {
    systemsQuery: AnyQueryConnection;
    craCount: number;
}

const defaultQuery: AnyQueryConnection = {
    nodes: [],
    loading: false,
    error: undefined,
    raw: undefined,
    refetch: async () =>
        ({ data: undefined, loading: false, networkStatus: 7 }) as any,
};

const GlobalDataContext = createContext<GlobalDataContextValue>({
    systemsQuery: defaultQuery,
    craCount: 0,
});

export function useGlobalData(): GlobalDataContextValue {
    return useContext(GlobalDataContext);
}

export function GlobalDataProvider({
    children,
}: {
    children: ReactNode;
}): ReactNode {
    const { pid } = useParams();

    const systemsQuery = useQueryConnection(
        GET_PROJECT_SYSTEMS_MENU,
        { pid: pid ?? '' },
        { skip: !pid },
    );

    const craQuery = useQuery(GET_PROJECT_CRA_COUNT, {
        variables: { pid: pid ?? '' },
        skip: !pid,
    });

    const craCount = craQuery.data?.crasForProject.length ?? 0;

    return (
        <GlobalDataContext.Provider value={{ systemsQuery, craCount }}>
            {children}
        </GlobalDataContext.Provider>
    );
}
